import React, { useEffect, useRef, useState } from 'react';
import Vapi from '@vapi-ai/web';
import { Loader2, Mic, Phone, PhoneOff, Video } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { API_BASE_URL } from '../config';

const initialForm = {
  name: '',
  phone: '',
  email: '',
  current_title: '',
  experience_summary: '',
  ai_assisted: true,
  recording: true,
  privacy_acknowledged: false
};

function QRJobLanding() {
  const { token } = useParams();
  const [job, setJob] = useState(null);
  const [candidate, setCandidate] = useState(null);
  const [knownCandidate, setKnownCandidate] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(true);
  const [submittingChannel, setSubmittingChannel] = useState('');
  const [error, setError] = useState('');
  const [started, setStarted] = useState(null);
  const [browserCallState, setBrowserCallState] = useState('idle');
  const [liveTranscript, setLiveTranscript] = useState([]);
  const vapiRef = useRef(null);
  const browserSessionRef = useRef(null);

  useEffect(() => {
    let isMounted = true;

    async function loadJob() {
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}/api/public/qr/${token}`);
        const data = await response.json();
        if (!response.ok || !data.success) {
          throw new Error(data.error || 'This QR link is no longer available.');
        }

        if (isMounted) {
          setJob(data.job);
          setCandidate(data.candidate || null);
          setKnownCandidate(Boolean(data.known_candidate));
          if (data.candidate) {
            setForm((current) => ({
              ...current,
              name: data.candidate.name || current.name,
              current_title: data.candidate.current_title || current.current_title,
              experience_summary: data.candidate.experience_summary || current.experience_summary
            }));
          }
          setError('');
        }
      } catch (err) {
        if (isMounted) setError(err.message);
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    loadJob();
    return () => {
      isMounted = false;
    };
  }, [token]);

  useEffect(() => {
    const publicKey = process.env.REACT_APP_VAPI_PUBLIC_KEY;
    if (!publicKey) return undefined;

    const vapi = new Vapi(publicKey);
    vapiRef.current = vapi;

    const reportStatus = async (status, details = {}) => {
      const session = browserSessionRef.current;
      if (!session) return;
      try {
        await fetch(
          `${API_BASE_URL}/api/public/qr/${token}/browser-session/${session.sessionId}`,
          {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              'X-QR-Session-Token': session.accessToken
            },
            body: JSON.stringify({ status, ...details })
          }
        );
      } catch (statusError) {
        console.error('Unable to update ALICE browser session', statusError);
      }
    };

    vapi.on('call-start', () => {
      setBrowserCallState('connected');
      reportStatus('in_progress');
    });
    vapi.on('call-start-success', (event) => {
      if (event?.callId) reportStatus('in_progress', { call_id: event.callId });
    });
    vapi.on('call-end', () => {
      setBrowserCallState('ended');
      reportStatus('completed');
    });
    vapi.on('message', (message) => {
      if (message?.type === 'transcript' && message?.transcriptType === 'final') {
        setLiveTranscript((current) => [
          ...current,
          { role: message.role, text: message.transcript }
        ]);
      }
    });
    vapi.on('error', (callError) => {
      const details =
        callError?.error?.message ||
        callError?.error?.response?.data?.message ||
        callError?.response?.data?.message ||
        callError?.response?.data?.error ||
        callError?.message;
      const message = Array.isArray(details)
        ? details.join(' ')
        : details || 'The browser conversation could not continue.';
      console.error('ALICE browser call failed', callError);
      setError(message);
      setBrowserCallState('failed');
      reportStatus('failed', { error: message });
    });

    return () => {
      vapi.stop();
      vapiRef.current = null;
    };
  }, [token]);

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const needsPhoneConfirmation = !knownCandidate || !candidate?.has_phone;

  const startBrowserConversation = async () => {
    setSubmittingChannel('web_voice');
    setError('');
    setLiveTranscript([]);

    try {
      if (!process.env.REACT_APP_VAPI_PUBLIC_KEY) {
        throw new Error('Browser voice is not configured yet. Please request a phone call.');
      }
      if (!form.ai_assisted || !form.privacy_acknowledged) {
        throw new Error('Please confirm consent before starting with ALICE.');
      }

      const response = await fetch(
        `${API_BASE_URL}/api/public/qr/${token}/start-browser-session`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: form.name,
            email: form.email,
            current_title: form.current_title,
            experience_summary: form.experience_summary,
            consent: {
              ai_assisted: form.ai_assisted,
              recording: form.recording,
              privacy_acknowledged: form.privacy_acknowledged
            }
          })
        }
      );
      const session = await response.json();
      if (!response.ok || !session.success) {
        throw new Error(session.error || 'ALICE could not prepare the browser conversation.');
      }

      browserSessionRef.current = {
        sessionId: session.session_id,
        accessToken: session.session_access_token
      };
      setBrowserCallState('connecting');
      await vapiRef.current.start(session.assistant_id, session.assistant_overrides);
    } catch (startError) {
      setBrowserCallState('failed');
      setError(startError.message);
    } finally {
      setSubmittingChannel('');
    }
  };

  const endBrowserConversation = () => {
    vapiRef.current?.stop();
  };

  const startAlice = async (channel) => {
    setSubmittingChannel(channel);
    setError('');

    try {
      if (!form.ai_assisted || !form.privacy_acknowledged) {
        throw new Error('Please confirm consent before starting with ALICE.');
      }

      const response = await fetch(`${API_BASE_URL}/api/public/qr/${token}/start-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          channel,
          name: form.name,
          phone: form.phone,
          email: form.email,
          current_title: form.current_title,
          experience_summary: form.experience_summary,
          consent: {
            ai_assisted: form.ai_assisted,
            recording: form.recording,
            privacy_acknowledged: form.privacy_acknowledged
          }
        })
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'ALICE could not start the session.');
      }
      setStarted({ ...data, channel });
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmittingChannel('');
    }
  };

  if (loading) {
    return (
      <main className="qr-alice-page qr-center">
        <Loader2 className="qr-spinner" aria-label="Loading" />
      </main>
    );
  }

  if (error && !job) {
    return (
      <main className="qr-alice-page qr-center">
        <section className="qr-error-card">
          <h1>QR link unavailable</h1>
          <p>{error}</p>
        </section>
      </main>
    );
  }

  return (
    <main className="qr-alice-page">
      <section className="qr-shell" aria-labelledby="qr-title">
        <header className="qr-header">
          <p className="qr-eyebrow">
            {[job?.title, job?.location].filter(Boolean).join(' · ') || 'Role Opportunity'}
          </p>
          <h1 id="qr-title">Speak with ALICE</h1>
          <p className="qr-subtext">
            Ask questions about this role and check fit with ALICE directly in your browser,
            or request a phone call.
          </p>
        </header>

        {job?.description && (
          <section className="qr-job-card">
            <strong>{job.company || 'Role brief'}</strong>
            <p>{job.description}</p>
          </section>
        )}

        {started ? (
          <section className="qr-started-card">
            <Phone aria-hidden="true" />
            <div>
              <h2>ALICE is calling</h2>
              <p>Your voice session has started. Please keep your phone nearby for the AI recruiter call.</p>
            </div>
          </section>
        ) : (
          <>
            {knownCandidate ? (
              <section className="qr-known-card">
                <p>
                  {candidate?.name ? `Hi ${candidate.name},` : 'Hi,'} ALICE can speak with you about this role.
                </p>
                <span>
                  We already have your outreach details from this JD email.
                  {candidate?.has_phone && candidate?.phone_last4
                    ? ` The call can use the saved number ending in ${candidate.phone_last4}.`
                    : ' Please add the best phone number for a call.'}
                </span>
              </section>
            ) : (
              <section className="qr-form-grid" aria-label="Candidate details">
                <label>
                  Full name
                  <input
                    required
                    value={form.name}
                    onChange={(event) => updateField('name', event.target.value)}
                  />
                </label>
                <label>
                  Email
                  <input
                    type="email"
                    value={form.email}
                    onChange={(event) => updateField('email', event.target.value)}
                  />
                </label>
                <label>
                  Current title
                  <input
                    value={form.current_title}
                    onChange={(event) => updateField('current_title', event.target.value)}
                  />
                </label>
              </section>
            )}

            {needsPhoneConfirmation && (
              <section className="qr-phone-card">
                <label>
                  Phone number (only needed for a phone call)
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(event) => updateField('phone', event.target.value)}
                  />
                </label>
              </section>
            )}

            {!knownCandidate && (
              <section className="qr-phone-card">
                <label>
                  Experience summary
                  <textarea
                    rows={3}
                    value={form.experience_summary}
                    onChange={(event) => updateField('experience_summary', event.target.value)}
                  />
                </label>
              </section>
            )}

            <section className="qr-consent-card" aria-label="Consent">
              <label>
                <input
                  type="checkbox"
                  checked={form.ai_assisted}
                  onChange={(event) => updateField('ai_assisted', event.target.checked)}
                />
                I agree to speak with ALICE, an AI-assisted recruiter.
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={form.recording}
                  onChange={(event) => updateField('recording', event.target.checked)}
                />
                I consent to call recording or transcript capture where applicable.
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={form.privacy_acknowledged}
                  onChange={(event) => updateField('privacy_acknowledged', event.target.checked)}
                />
                I acknowledge that my details will be used for this recruitment conversation.
              </label>
            </section>

            {error && <p className="qr-inline-error">{error}</p>}

            <section className="qr-stage" aria-label="Choose how to speak with ALICE">
              <article className="qr-choice qr-choice-left">
                <div className="qr-icon qr-icon-call"><Mic size={22} aria-hidden="true" /></div>
                <h2>Talk Now</h2>
                <p>Speak with ALICE here using your device microphone and speaker.</p>
                <button
                  type="button"
                  className="qr-button qr-button-call"
                  disabled={Boolean(submittingChannel) || browserCallState === 'connecting'}
                  onClick={browserCallState === 'connected' ? endBrowserConversation : startBrowserConversation}
                >
                  {browserCallState === 'connected' ? (
                    <><PhoneOff size={16} /> End Conversation</>
                  ) : submittingChannel === 'web_voice' || browserCallState === 'connecting' ? (
                    <><Loader2 size={16} className="qr-spin" /> Connecting</>
                  ) : (
                    <><Mic size={16} /> Talk to ALICE Now</>
                  )}
                </button>
              </article>

              <div className="qr-avatar-wrap" aria-label="ALICE avatar">
                <div className="qr-avatar-glow" aria-hidden="true" />
                <img className="qr-avatar" src="/hero-character.jpeg" alt="ALICE AI recruiter avatar" />
                <div className="qr-status">
                  <span aria-hidden="true" />
                  Ready now
                </div>
              </div>

              <article className="qr-choice qr-choice-right">
                <div className="qr-icon qr-icon-phone"><Phone size={22} aria-hidden="true" /></div>
                <h2>Request Call</h2>
                <p>Prefer your phone? ALICE can call your saved number or the number entered above.</p>
                <button
                  type="button"
                  className="qr-button qr-button-phone"
                  disabled={Boolean(submittingChannel) || browserCallState === 'connected'}
                  onClick={() => startAlice('voice')}
                >
                  {submittingChannel === 'voice' ? <Loader2 size={16} className="qr-spin" /> : <Phone size={16} />}
                  Request Phone Call
                </button>
              </article>
            </section>

            {browserCallState === 'connected' && (
              <section className="qr-live-card" aria-live="polite">
                <div className="qr-live-heading"><Mic size={18} /> Live with ALICE</div>
                <p>{liveTranscript.length
                  ? liveTranscript[liveTranscript.length - 1].text
                  : 'You are connected. ALICE is listening.'}</p>
              </section>
            )}

            <div className="qr-video-note">
              <Video size={17} aria-hidden="true" />
              <span><strong>Live video</strong> is coming soon.</span>
            </div>
          </>
        )}

        <footer className="qr-details" aria-label="Session details">
          <span>AI-assisted recruiter conversation</span>
          <span>Consent required before starting</span>
          <span>No application form needed</span>
        </footer>
      </section>

      <style>{`
        .qr-alice-page {
          min-height: 100vh;
          padding: 28px;
          display: grid;
          place-items: center;
          color: #101820;
          background:
            radial-gradient(circle at 50% 18%, rgba(246, 180, 75, 0.2), transparent 34%),
            linear-gradient(180deg, #ffffff 0%, #eef3f8 100%);
        }
        .qr-center { justify-content: center; }
        .qr-shell {
          width: min(1080px, 100%);
          display: grid;
          gap: 20px;
          padding: clamp(22px, 4vw, 48px);
          border: 1px solid rgba(216, 222, 230, 0.9);
          border-radius: 8px;
          background: rgba(255, 255, 255, 0.84);
          box-shadow: 0 22px 70px rgba(16, 24, 32, 0.14);
          backdrop-filter: blur(18px);
        }
        .qr-header { text-align: center; }
        .qr-eyebrow {
          margin: 0 0 10px;
          font-size: 12px;
          font-weight: 800;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: #084fc0;
        }
        .qr-header h1 {
          margin: 0;
          font-size: clamp(34px, 6vw, 64px);
          line-height: 0.96;
          font-weight: 850;
          letter-spacing: 0;
        }
        .qr-subtext {
          max-width: 620px;
          margin: 16px auto 0;
          font-size: clamp(15px, 2vw, 18px);
          line-height: 1.55;
          color: #5f6975;
        }
        .qr-job-card,
        .qr-known-card,
        .qr-phone-card,
        .qr-consent-card,
        .qr-started-card,
        .qr-error-card {
          border: 1px solid #d8dee6;
          border-radius: 8px;
          background: #fff;
          box-shadow: 0 12px 34px rgba(16, 24, 32, 0.08);
        }
        .qr-job-card { padding: 18px; }
        .qr-job-card strong { display: block; margin-bottom: 6px; }
        .qr-job-card p { margin: 0; color: #5f6975; line-height: 1.55; }
        .qr-known-card {
          padding: 18px;
          border-color: #bfe2ff;
          background: #f0f8ff;
        }
        .qr-known-card p { margin: 0 0 6px; font-weight: 800; }
        .qr-known-card span { color: #4f5c69; line-height: 1.5; }
        .qr-form-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 14px;
        }
        .qr-form-grid label,
        .qr-phone-card label,
        .qr-consent-card label {
          display: grid;
          gap: 8px;
          font-size: 14px;
          font-weight: 750;
          color: #2d3743;
        }
        .qr-phone-card { padding: 16px; }
        .qr-form-grid input,
        .qr-phone-card input,
        .qr-phone-card textarea {
          width: 100%;
          min-height: 42px;
          border: 1px solid #cfd7e2;
          border-radius: 6px;
          padding: 9px 11px;
          font: inherit;
          font-weight: 500;
          background: #fff;
        }
        .qr-phone-card textarea { resize: vertical; }
        .qr-consent-card {
          padding: 16px;
          gap: 12px;
          display: grid;
          background: rgba(255,255,255,0.74);
        }
        .qr-consent-card label {
          grid-template-columns: 18px 1fr;
          align-items: start;
          font-weight: 650;
          color: #4f5c69;
          line-height: 1.45;
        }
        .qr-consent-card input { margin-top: 2px; }
        .qr-inline-error {
          margin: 0;
          padding: 10px 12px;
          border: 1px solid #fecaca;
          border-radius: 6px;
          color: #b42318;
          background: #fff1f0;
          font-size: 14px;
        }
        .qr-stage {
          display: grid;
          grid-template-columns: minmax(170px, 1fr) minmax(260px, 380px) minmax(170px, 1fr);
          align-items: center;
          gap: clamp(18px, 4vw, 36px);
        }
        .qr-avatar-wrap {
          position: relative;
          display: grid;
          place-items: center;
          min-height: 390px;
        }
        .qr-avatar-glow {
          position: absolute;
          width: min(92%, 340px);
          aspect-ratio: 1;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(246, 180, 75, 0.34), rgba(15, 111, 255, 0.08) 54%, transparent 70%);
        }
        .qr-avatar {
          position: relative;
          width: min(82vw, 340px);
          aspect-ratio: 1;
          border-radius: 50%;
          object-fit: cover;
          object-position: center;
          border: 8px solid #fff;
          box-shadow: 0 28px 72px rgba(16, 24, 32, 0.28), 0 0 0 1px rgba(16, 24, 32, 0.08);
          animation: qrFloat 5.8s ease-in-out infinite;
        }
        .qr-status {
          position: absolute;
          bottom: 24px;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 9px 12px;
          border-radius: 999px;
          border: 1px solid rgba(216, 222, 230, 0.95);
          background: rgba(255,255,255,0.92);
          box-shadow: 0 10px 28px rgba(16, 24, 32, 0.12);
          font-size: 13px;
          font-weight: 750;
        }
        .qr-status span {
          width: 9px;
          height: 9px;
          border-radius: 999px;
          background: #07835f;
          box-shadow: 0 0 0 5px rgba(7, 131, 95, 0.12);
        }
        .qr-choice {
          min-height: 210px;
          display: flex;
          flex-direction: column;
          justify-content: center;
          gap: 14px;
          padding: 22px;
          border: 1px solid #d8dee6;
          border-radius: 8px;
          background: #fff;
          box-shadow: 0 12px 34px rgba(16, 24, 32, 0.08);
        }
        .qr-choice-left { align-items: flex-end; text-align: right; }
        .qr-choice-right { align-items: flex-start; text-align: left; }
        .qr-icon {
          width: 44px;
          height: 44px;
          display: grid;
          place-items: center;
          border-radius: 999px;
          color: #fff;
        }
        .qr-icon-call { background: #07835f; }
        .qr-icon-phone { background: #084fc0; }
        .qr-icon-video { background: #0f6fff; }
        .qr-choice h2 { margin: 0; font-size: 22px; line-height: 1.1; }
        .qr-choice p { max-width: 235px; margin: 0; color: #5f6975; line-height: 1.45; font-size: 14px; }
        .qr-button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 9px;
          min-height: 46px;
          padding: 0 18px;
          border: 0;
          border-radius: 6px;
          color: #fff;
          font: inherit;
          font-size: 14px;
          font-weight: 800;
          cursor: pointer;
        }
        .qr-button:disabled { cursor: not-allowed; opacity: 0.65; }
        .qr-button-call { background: #07835f; }
        .qr-button-phone { background: #084fc0; }
        .qr-button-video { background: #0f6fff; }
        .qr-button-video:disabled {
          background: #8ca3c7;
        }
        .qr-live-card {
          display: grid;
          gap: 8px;
          padding: 16px 18px;
          border: 1px solid #9fd8c7;
          border-radius: 8px;
          background: #effaf6;
        }
        .qr-live-heading {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #056348;
          font-weight: 800;
        }
        .qr-live-card p { margin: 0; color: #34434e; line-height: 1.5; }
        .qr-video-note {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          color: #5f6975;
          font-size: 14px;
        }
        .qr-details {
          display: flex;
          justify-content: center;
          flex-wrap: wrap;
          gap: 10px;
          color: #5f6975;
          font-size: 13px;
        }
        .qr-details span {
          display: inline-flex;
          align-items: center;
          min-height: 32px;
          padding: 0 12px;
          border: 1px solid #d8dee6;
          border-radius: 999px;
          background: rgba(255,255,255,0.72);
          white-space: nowrap;
        }
        .qr-started-card {
          display: flex;
          gap: 14px;
          align-items: flex-start;
          padding: 22px;
          max-width: 680px;
          margin: 0 auto;
        }
        .qr-started-card svg { color: #07835f; flex: 0 0 auto; margin-top: 3px; }
        .qr-started-card h2 { margin: 0 0 7px; }
        .qr-started-card p { margin: 0; color: #5f6975; line-height: 1.55; }
        .qr-error-card { max-width: 430px; padding: 24px; }
        .qr-error-card h1 { margin: 0 0 10px; font-size: 24px; }
        .qr-error-card p { margin: 0; color: #5f6975; line-height: 1.5; }
        .qr-spinner, .qr-spin {
          animation: qrSpin 900ms linear infinite;
        }
        .qr-spinner {
          width: 34px;
          height: 34px;
          color: #101820;
        }
        @keyframes qrFloat {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        @keyframes qrSpin {
          to { transform: rotate(360deg); }
        }
        @media (max-width: 860px) {
          .qr-alice-page { padding: 14px; }
          .qr-stage, .qr-form-grid { grid-template-columns: 1fr; }
          .qr-avatar-wrap { min-height: 320px; order: 1; }
          .qr-choice { min-height: auto; align-items: stretch; text-align: left; }
          .qr-choice-left, .qr-choice-right { align-items: stretch; text-align: left; }
          .qr-choice-left { order: 2; }
          .qr-choice-right { order: 3; }
          .qr-choice p { max-width: none; }
          .qr-button { width: 100%; }
          .qr-status { bottom: 6px; }
          .qr-details span { white-space: normal; }
        }
      `}</style>
    </main>
  );
}

export default QRJobLanding;
